import React, { useEffect, useRef, useState, useCallback } from 'react';
import InstallerLayout from '../components/InstallerLayout';
import api from '../api/axios';
import { C } from '../theme';

const SCAN_INTERVAL_MS = 400;

export default function GetDevice() {
  const videoRef   = useRef(null);
  const streamRef  = useRef(null);
  const timerRef   = useRef(null);
  const detectorRef = useRef(null);

  const [camActive,   setCamActive]   = useState(false);
  const [camError,    setCamError]    = useState('');
  const [supported,   setSupported]   = useState(true);
  const [manualSerial, setManualSerial] = useState('');

  const [scanning,    setScanning]    = useState(false);   // waiting for detect result
  const [result,      setResult]      = useState(null);    // { success, package_id, name, items, message }
  const [dispatchErr, setDispatchErr] = useState('');
  const [dispatching, setDispatching] = useState(false);

  // Check BarcodeDetector support
  useEffect(() => {
    if (!('BarcodeDetector' in window)) setSupported(false);
    else {
      window.BarcodeDetector.getSupportedFormats().then(fmts => {
        detectorRef.current = new window.BarcodeDetector({
          formats: fmts.includes('code_128') ? ['code_128'] : fmts,
        });
      });
    }
    return () => stopCamera();
  }, []);

  const stopCamera = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setCamActive(false);
  }, []);

  const startCamera = async () => {
    setCamError(''); setResult(null); setDispatchErr('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamActive(true);

      if (supported && detectorRef.current) {
        timerRef.current = setInterval(async () => {
          if (!videoRef.current || videoRef.current.readyState < 3) return;
          if (scanning) return;
          try {
            const results = await detectorRef.current.detect(videoRef.current);
            if (results.length > 0) {
              const raw = results[0].rawValue;
              stopCamera();
              await doDispatch(raw);
            }
          } catch {}
        }, SCAN_INTERVAL_MS);
      }
    } catch (e) {
      setCamError(e.name === 'NotAllowedError' ? 'Camera permission denied. Please allow camera access.' : `Camera error: ${e.message}`);
    }
  };

  const doDispatch = async (serial) => {
    setScanning(true); setDispatchErr(''); setResult(null);
    try {
      const { data } = await api.post('/api/installer/scan-dispatch', { serial_number: serial });
      setResult({ ...data, scannedSerial: serial });
    } catch (e) {
      setDispatchErr(e?.response?.data?.detail || 'Dispatch failed. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleManual = async (e) => {
    e.preventDefault();
    const s = manualSerial.trim();
    if (!s) return;
    setManualSerial('');
    await doDispatch(s);
  };

  const reset = () => { setResult(null); setDispatchErr(''); setManualSerial(''); };

  return (
    <InstallerLayout title="Get Device">

      <div style={{ maxWidth: '640px', margin: '0 auto' }}>

        {/* ── Success result ── */}
        {result && (
          <div style={{ background: C.white, border: `2px solid #6D28D9`, borderRadius: '16px', overflow: 'hidden', marginBottom: '20px' }}>
            <div style={{ padding: '16px 22px', background: '#F3E8FF', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '22px' }}>✅</span>
              <div>
                <div style={{ fontWeight: 700, color: '#6D28D9', fontSize: '14px' }}>Package Dispatched Successfully</div>
                <div style={{ fontSize: '12px', color: '#7C3AED' }}>{result.message}</div>
              </div>
            </div>
            <div style={{ padding: '20px 22px' }}>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
                <div style={statBox('#F3E8FF', '#6D28D9')}>
                  <div style={{ fontSize: '11px', color: '#7C3AED', fontWeight: 600, marginBottom: '2px' }}>Package ID</div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '15px', color: '#6D28D9' }}>{result.package_id}</div>
                </div>
                {result.name && (
                  <div style={statBox(C.primaryLight, C.primaryDeep)}>
                    <div style={{ fontSize: '11px', color: C.primaryDeep, fontWeight: 600, marginBottom: '2px' }}>Name</div>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: C.primaryDeep }}>{result.name}</div>
                  </div>
                )}
              </div>

              {/* Items in this package */}
              {result.items?.length > 0 && (
                <div>
                  <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                    Package Contents
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {result.items.map((it, i) => (
                      <div key={it.inventory_item_id} style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '8px 12px', borderRadius: '8px',
                        background: i === 0 ? '#FFF7ED' : i === 1 ? '#F5F3FF' : '#EFF6FF',
                        border: `1px solid ${i === 0 ? '#FED7AA' : i === 1 ? '#DDD6FE' : '#BFDBFE'}`,
                      }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: i === 0 ? C.primary : i === 1 ? '#6D28D9' : '#2563EB' }}>
                          {it.category_name}
                        </span>
                        <span style={{ fontFamily: 'monospace', fontSize: '12px', color: C.textMain, fontWeight: 600 }}>
                          {it.serial_number}
                        </span>
                        {it.ref_number && (
                          <span style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub }}>· Ref: {it.ref_number}</span>
                        )}
                        {it.type_name && <span style={{ fontSize: '11px', color: C.textSub, marginLeft: 'auto' }}>{it.type_name}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{ padding: '14px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '10px' }}>
              <button onClick={reset} style={{
                flex: 1, padding: '11px', background: `linear-gradient(135deg, #8B5CF6, #6D28D9)`,
                color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              }}>
                📷 Scan Another Package
              </button>
            </div>
          </div>
        )}

        {/* ── Error ── */}
        {dispatchErr && (
          <div style={{
            background: C.dangerBg, border: `1px solid #FCA5A5`, color: C.dangerText,
            borderRadius: '10px', padding: '14px 18px', marginBottom: '16px',
            fontSize: '13px', fontWeight: 600,
          }}>
            ⚠ {dispatchErr}
            <button onClick={reset} style={{ marginLeft: '12px', background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontWeight: 700, fontSize: '12px' }}>
              Retry
            </button>
          </div>
        )}

        {/* ── Scanning spinner ── */}
        {scanning && (
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '32px', textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>⏳</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: C.textMain }}>Dispatching package…</div>
          </div>
        )}

        {/* ── Camera area ── */}
        {!result && !scanning && (
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '16px', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ padding: '18px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: '#F3E8FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>📷</div>
              <div>
                <div style={{ fontWeight: 700, color: C.textMain, fontSize: '14px' }}>Scan Package Barcode</div>
                <div style={{ fontSize: '12px', color: C.textSub }}>Point camera at the barcode printed on the package label</div>
              </div>
            </div>

            <div style={{ padding: '20px 22px' }}>

              {/* Video preview */}
              <div style={{
                position: 'relative', borderRadius: '12px', overflow: 'hidden',
                background: '#111', aspectRatio: '16/9',
                border: `2px solid ${camActive ? '#8B5CF6' : C.border}`,
                marginBottom: '16px',
              }}>
                <video
                  ref={videoRef}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: camActive ? 'block' : 'none' }}
                  playsInline muted
                />

                {/* Scan frame overlay */}
                {camActive && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <div style={{ position: 'relative', width: '70%', maxWidth: '300px', height: '100px' }}>
                      {/* Corner marks */}
                      {[
                        { top: 0, left: 0, borderTop: '3px solid #8B5CF6', borderLeft: '3px solid #8B5CF6' },
                        { top: 0, right: 0, borderTop: '3px solid #8B5CF6', borderRight: '3px solid #8B5CF6' },
                        { bottom: 0, left: 0, borderBottom: '3px solid #8B5CF6', borderLeft: '3px solid #8B5CF6' },
                        { bottom: 0, right: 0, borderBottom: '3px solid #8B5CF6', borderRight: '3px solid #8B5CF6' },
                      ].map((s, i) => (
                        <div key={i} style={{ position: 'absolute', width: '20px', height: '20px', borderRadius: '2px', ...s }} />
                      ))}
                      {/* Scan line animation */}
                      <div style={{
                        position: 'absolute', left: '4px', right: '4px', height: '2px',
                        background: 'rgba(139,92,246,0.8)',
                        animation: 'scanLine 2s linear infinite',
                        top: '50%',
                      }} />
                    </div>
                    <div style={{
                      position: 'absolute', bottom: '12px', left: 0, right: 0,
                      textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 500,
                    }}>
                      {supported ? 'Scanning for barcode…' : 'Camera preview (use manual entry below)'}
                    </div>
                  </div>
                )}

                {!camActive && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)' }}>
                    <div style={{ fontSize: '40px', marginBottom: '8px' }}>📷</div>
                    <div style={{ fontSize: '13px' }}>Camera is off</div>
                  </div>
                )}
              </div>

              {/* Scan line keyframe injected once */}
              <style>{`@keyframes scanLine { 0%{top:10%} 50%{top:85%} 100%{top:10%} }`}</style>

              {camError && (
                <div style={{ background: C.dangerBg, color: C.dangerText, borderRadius: '8px', padding: '10px 14px', fontSize: '12px', marginBottom: '12px' }}>
                  ⚠ {camError}
                </div>
              )}

              {/* Camera toggle button */}
              {!camActive ? (
                <button onClick={startCamera} style={{
                  width: '100%', padding: '13px',
                  background: `linear-gradient(135deg, #8B5CF6, #6D28D9)`,
                  color: '#fff', border: 'none', borderRadius: '10px',
                  fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(109,40,217,0.3)', marginBottom: '12px',
                }}>
                  📷 Open Camera
                </button>
              ) : (
                <button onClick={stopCamera} style={{
                  width: '100%', padding: '13px',
                  background: C.dangerBg, color: C.dangerText,
                  border: `1px solid #FCA5A5`, borderRadius: '10px',
                  fontSize: '13px', fontWeight: 700, cursor: 'pointer', marginBottom: '12px',
                }}>
                  ✕ Close Camera
                </button>
              )}

              {/* Manual entry */}
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '14px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: C.textSub, marginBottom: '8px' }}>
                  {supported ? '⌨ Or enter serial number manually:' : '⌨ Manual entry (scanning not supported in this browser):'}
                </div>
                <form onSubmit={handleManual} style={{ display: 'flex', gap: '8px' }}>
                  <input
                    value={manualSerial}
                    onChange={e => setManualSerial(e.target.value)}
                    placeholder="Type or paste IMEI / serial number…"
                    style={{
                      flex: 1, padding: '10px 12px',
                      border: `1.5px solid ${C.border}`, borderRadius: '8px',
                      fontSize: '13px', fontFamily: 'monospace', outline: 'none',
                    }}
                  />
                  <button type="submit" disabled={!manualSerial.trim()} style={{
                    padding: '10px 18px',
                    background: manualSerial.trim() ? `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})` : '#E5E7EB',
                    color: manualSerial.trim() ? '#fff' : '#9CA3AF',
                    border: 'none', borderRadius: '8px',
                    fontSize: '13px', fontWeight: 700, cursor: manualSerial.trim() ? 'pointer' : 'not-allowed',
                    flexShrink: 0,
                  }}>
                    Dispatch
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </InstallerLayout>
  );
}

const statBox = (bg, color) => ({
  flex: 1, minWidth: '120px', padding: '12px 14px',
  background: bg, borderRadius: '10px',
  border: `1px solid ${color}30`,
});
